(function (window, document) {
  'use strict'

  /*
   * bootstrap-swal usage guide
   *
   * Basic calls:
   *   Swal.fire('Title')
   *   Swal.fire('Title', 'Message', 'warning')
   *   Swal.fire({ title: 'Title', html: '<b>HTML</b>' })
   *
   * Promise result shape:
   *   { isConfirmed, isDenied, isDismissed, value, dismiss }
   *
   * Main options (object form):
   *   title, text, html, icon
   *   showConfirmButton, showCancelButton, showDenyButton
   *   confirmButtonText, cancelButtonText, denyButtonText
   *   showCloseButton, allowEscapeKey, focusConfirm
   *   enforceFocus: true to enable Bootstrap focus trap (default: false)
   *   preConfirm: async/sync callback, false blocks close
   *   preDeny: async/sync callback, false blocks close
   *   didOpen, willClose, didClose
   *   customClass.popup, showClass.popup, hideClass.popup
   *   toast, timer
   *
   * Size handling:
   *   - Explicit size: size/bootstrapSize/modalSize = 'sm' | 'md' | 'lg' | 'xl'
   *   - Explicit width: width = number|string (px or css width)
   *   - autoSize=false keeps explicit width even for small dialogs
   *   - Auto mode (default):
   *       short/simple dialogs => modal-sm
   *       complex forms => default bootstrap width
   *       table/long content => modal-lg
   *
   * Extra API:
   *   Swal.close()              // closes top modal
   *   Swal.close(targetNode)    // closes a specific popup/container/child node
   *   Swal.getPopup()           // returns active popup node
   *   Swal.showValidationMessage('msg')
   *   Swal.mixin(defaults)      // create derived Swal instance
   */

  var modalStack = []

  function getTopActive() {
    return modalStack.length ? modalStack[modalStack.length - 1] : null
  }

  function sanitizeText(value) {
    return String(value || '').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
  }

  function toObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    return value
  }

  function mergeOptions(base, extra) {
    var out = Object.assign({}, toObject(base), toObject(extra))
    out.customClass = Object.assign({}, toObject(base && base.customClass), toObject(extra && extra.customClass))
    out.showClass = Object.assign({}, toObject(base && base.showClass), toObject(extra && extra.showClass))
    out.hideClass = Object.assign({}, toObject(base && base.hideClass), toObject(extra && extra.hideClass))
    return out
  }

  function normalizeFireArgs(args) {
    if (!args.length) return {}
    if (typeof args[0] === 'object' && args[0] !== null && !Array.isArray(args[0])) {
      return Object.assign({}, args[0])
    }
    var title = String(args[0] || '')
    var text = args.length > 1 ? String(args[1] || '') : ''
    var icon = args.length > 2 ? String(args[2] || '') : ''
    return {
      title: title,
      text: text,
      icon: icon
    }
  }

  function escapeHtml(text) {
    return String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function stripModalBackdrop() {
    var nodes = document.querySelectorAll('.modal-backdrop')
    nodes.forEach(function (node) {
      try { node.remove() } catch (_e) {}
    })
    document.body.classList.remove('modal-open')
    document.body.style.removeProperty('padding-right')
  }

  function normalizeDialogSizeClass(raw) {
    var size = String(raw || '').trim().toLowerCase()
    if (!size) return ''
    if (size === 'sm' || size === 'small' || size === 'modal-sm') return 'modal-sm'
    if (size === 'lg' || size === 'large' || size === 'modal-lg') return 'modal-lg'
    if (size === 'xl' || size === 'xlarge' || size === 'modal-xl') return 'modal-xl'
    if (size === 'md' || size === 'medium' || size === 'default' || size === 'normal') return ''
    return ''
  }

  function stripHtmlTags(html) {
    return String(html || '')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  function ensurePositionStyles() {
    if (document.getElementById('swal-position-styles')) return
    var style = document.createElement('style')
    style.id = 'swal-position-styles'
    style.textContent = [
      '.modal[data-swal-pos^="bottom"]{display:flex!important;align-items:flex-end;}',
      '.modal[data-swal-pos="bottom"],.modal[data-swal-pos="bottom-center"]{justify-content:center;}',
      '.modal[data-swal-pos="bottom-left"]{justify-content:flex-start;}',
      '.modal[data-swal-pos="bottom-right"]{justify-content:flex-end;}',
      '.modal[data-swal-pos^="bottom"] .modal-dialog{margin:0 1rem 1rem;}'
    ].join('')
    document.head.appendChild(style)
  }

  function getPortalNavbarBottom() {
    var navbar = document.querySelector('[data-modal-max-top]')
    if (!navbar) return 0
    if (navbar.offsetParent === null) return 0
    var rect = navbar.getBoundingClientRect()
    if (!rect || !Number.isFinite(rect.bottom)) return 0
    return Math.max(0, Math.ceil(rect.bottom))
  }

  function applyNavbarClearance(container, dialog) {
    if (!container || !dialog) return
    var navBottom = getPortalNavbarBottom()
    container.style.paddingTop = navBottom > 0 ? (String(navBottom) + 'px') : ''
    dialog.style.maxHeight = navBottom > 0 ? ('calc(100vh - ' + String(navBottom) + 'px)') : ''
  }

  function resolveDialogSizeClass(opts) {
    if (!opts) return ''

    var explicitSize = normalizeDialogSizeClass(opts.bootstrapSize || opts.modalSize || opts.size)
    if (explicitSize) return explicitSize

    var html = String(opts.html || '')
    var text = String(opts.text || '')
    var title = (opts.titleText !== undefined && opts.titleText !== null)
      ? String(opts.titleText || '')
      : stripHtmlTags(String(opts.title || ''))
    var plainContent = (title + ' ' + text + ' ' + stripHtmlTags(html)).trim()
    var plainLength = plainContent.length
    var hasWideTable = /<table|table-responsive|datatable|thead|tbody|colgroup/i.test(html)
    var hasComplexLayout = /<textarea|<select|<input|form-control|input-group|accordion|nav-tabs|tab-pane/i.test(html)
    var buttonCount = (opts.showConfirmButton === false ? 0 : 1) + (opts.showCancelButton ? 1 : 0) + (opts.showDenyButton ? 1 : 0)

    if (hasWideTable || plainLength > 1400) return 'modal-lg'
    if (hasComplexLayout || plainLength > 500 || buttonCount >= 3) return ''
    if (plainLength > 0 && plainLength <= 260) return 'modal-sm'
    return ''
  }

  function shouldApplyFixedWidth(opts, dialogSizeClass) {
    if (!opts || opts.width === undefined || opts.width === null || opts.width === '') return false
    if (opts.autoSize === false) return true
    if (dialogSizeClass === 'modal-sm' || dialogSizeClass === 'modal-lg' || dialogSizeClass === 'modal-xl') return false
    return true
  }

  function buildModalDom(opts) {
    var container = document.createElement('div')
    container.className = 'modal fade swal2-container'
    container.tabIndex = -1
    container.setAttribute('role', 'dialog')
    container.setAttribute('aria-modal', 'true')

    var dialog = document.createElement('div')
    var position = String(opts.position || 'bottom-right').toLowerCase().trim()
    var posV = position ? position.split('-')[0] : ''
    var useDialogCentered = (posV !== 'bottom' && posV !== 'top')
    dialog.className = useDialogCentered ? 'modal-dialog modal-dialog-centered' : 'modal-dialog'
    container.setAttribute('data-swal-pos', position)
    applyNavbarClearance(container, dialog)
    var dialogSizeClass = resolveDialogSizeClass(opts)
    if (dialogSizeClass) {
      dialog.classList.add(dialogSizeClass)
    }
    if (shouldApplyFixedWidth(opts, dialogSizeClass)) {
      var width = opts.width
      dialog.style.maxWidth = (typeof width === 'number') ? (String(width) + 'px') : String(width)
    }

    var content = document.createElement('div')
    content.className = 'modal-content swal2-popup'

    if (opts.customClass && opts.customClass.popup) {
      String(opts.customClass.popup).split(/\s+/).filter(Boolean).forEach(function (cls) {
        content.classList.add(cls)
      })
    }
    if (opts.showClass && opts.showClass.popup) {
      String(opts.showClass.popup).split(/\s+/).filter(Boolean).forEach(function (cls) {
        content.classList.add(cls)
      })
    }

    var closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.className = 'swal2-close btn-close'
    closeBtn.setAttribute('aria-label', 'Close this dialog')
    closeBtn.style.display = (opts.showCloseButton === false) ? 'none' : 'inline-block'

    var titleEl = document.createElement('h2')
    titleEl.className = 'swal2-title modal-title'
    titleEl.id = 'swal2-title'
    titleEl.style.display = 'block'
    if (opts.titleText !== undefined && opts.titleText !== null) {
      titleEl.textContent = sanitizeText(String(opts.titleText || ''))
    } else {
      titleEl.innerHTML = sanitizeText(String(opts.title || ''))
    }

    var iconEl = document.createElement('div')
    iconEl.className = 'swal2-icon'
    if (opts.icon) {
      iconEl.classList.add('swal2-' + String(opts.icon).toLowerCase())
      iconEl.style.display = 'flex'
      iconEl.innerHTML = '<div class="swal2-icon-content">' + escapeHtml(String(opts.icon || '').slice(0, 1).toUpperCase()) + '</div>'
    } else {
      iconEl.style.display = 'none'
    }

    var htmlContainer = document.createElement('div')
    htmlContainer.className = 'swal2-html-container text-start'
    htmlContainer.id = 'swal2-html-container'
    htmlContainer.style.display = 'block'
    if (opts.html !== undefined && opts.html !== null && opts.html !== '') {
      htmlContainer.innerHTML = sanitizeText(String(opts.html))
    } else if (opts.text !== undefined && opts.text !== null && opts.text !== '') {
      htmlContainer.textContent = sanitizeText(String(opts.text))
    } else {
      htmlContainer.innerHTML = ''
    }

    var progressSteps = document.createElement('ul')
    progressSteps.className = 'swal2-progress-steps'
    progressSteps.style.display = 'none'

    var imageEl = document.createElement('img')
    imageEl.className = 'swal2-image'
    imageEl.style.display = 'none'

    var validation = document.createElement('div')
    validation.className = 'swal2-validation-message alert alert-danger py-2 px-3 mt-2 mb-0'
    validation.id = 'swal2-validation-message'
    validation.style.display = 'none'

    var inputEl = document.createElement('input')
    inputEl.id = 'swal2-input'
    inputEl.className = 'swal2-input'
    inputEl.style.display = 'none'

    var fileEl = document.createElement('input')
    fileEl.type = 'file'
    fileEl.className = 'swal2-file'
    fileEl.style.display = 'none'

    var rangeWrap = document.createElement('div')
    rangeWrap.className = 'swal2-range'
    rangeWrap.style.display = 'none'
    rangeWrap.innerHTML = '<input type="range"><output></output>'

    var selectEl = document.createElement('select')
    selectEl.id = 'swal2-select'
    selectEl.className = 'swal2-select'
    selectEl.style.display = 'none'

    var radioEl = document.createElement('div')
    radioEl.className = 'swal2-radio'
    radioEl.style.display = 'none'

    var checkboxEl = document.createElement('label')
    checkboxEl.className = 'swal2-checkbox'
    checkboxEl.style.display = 'none'
    checkboxEl.innerHTML = '<input type="checkbox" id="swal2-checkbox"><span class="swal2-label"></span>'

    var textareaEl = document.createElement('textarea')
    textareaEl.id = 'swal2-textarea'
    textareaEl.className = 'swal2-textarea'
    textareaEl.style.display = 'none'

    var actions = document.createElement('div')
    actions.className = 'swal2-actions modal-footer'
    actions.style.display = 'flex'

    var loader = document.createElement('div')
    loader.className = 'swal2-loader'
    actions.appendChild(loader)

    var showConfirmButton = opts.showConfirmButton !== false
    var showDenyButton = !!opts.showDenyButton
    var showCancelButton = !!opts.showCancelButton

    var confirmBtn = document.createElement('button')
    confirmBtn.type = 'button'
    confirmBtn.className = 'swal2-confirm swal2-styled btn btn-primary'
    confirmBtn.textContent = sanitizeText(String(opts.confirmButtonText || 'OK'))
    confirmBtn.style.display = showConfirmButton ? 'inline-block' : 'none'

    var denyBtn = document.createElement('button')
    denyBtn.type = 'button'
    denyBtn.className = 'swal2-deny swal2-styled btn btn-warning'
    denyBtn.textContent = sanitizeText(String(opts.denyButtonText || 'Nem'))
    denyBtn.style.display = showDenyButton ? 'inline-block' : 'none'

    var cancelBtn = document.createElement('button')
    cancelBtn.type = 'button'
    cancelBtn.className = 'swal2-cancel swal2-styled btn btn-secondary'
    cancelBtn.textContent = sanitizeText(String(opts.cancelButtonText || 'Megse'))
    cancelBtn.style.display = showCancelButton ? 'inline-block' : 'none'

    actions.appendChild(confirmBtn)
    actions.appendChild(denyBtn)
    actions.appendChild(cancelBtn)

    content.appendChild(closeBtn)
    content.appendChild(progressSteps)
    content.appendChild(iconEl)
    content.appendChild(imageEl)
    content.appendChild(titleEl)
    content.appendChild(htmlContainer)
    content.appendChild(inputEl)
    content.appendChild(fileEl)
    content.appendChild(rangeWrap)
    content.appendChild(selectEl)
    content.appendChild(radioEl)
    content.appendChild(checkboxEl)
    content.appendChild(textareaEl)
    content.appendChild(validation)
    content.appendChild(actions)

    dialog.appendChild(content)
    container.appendChild(dialog)

    return {
      container: container,
      dialog: dialog,
      popup: content,
      validation: validation,
      closeBtn: closeBtn,
      confirmBtn: confirmBtn,
      denyBtn: denyBtn,
      cancelBtn: cancelBtn
    }
  }

  function showToast(opts) {
    var host = document.getElementById('portal-bootstrap-toast-host')
    if (!host) {
      host = document.createElement('div')
      host.id = 'portal-bootstrap-toast-host'
      host.className = 'toast-container position-fixed bottom-0 start-0 p-3'
      host.style.zIndex = '3200'
      document.body.appendChild(host)
    }

    var iconClass = 'text-bg-primary'
    var icon = String(opts.icon || '').toLowerCase()
    if (icon === 'success') iconClass = 'text-bg-success'
    else if (icon === 'error') iconClass = 'text-bg-danger'
    else if (icon === 'warning') iconClass = 'text-bg-warning'
    else if (icon === 'info') iconClass = 'text-bg-info'

    var toast = document.createElement('div')
    toast.className = 'toast align-items-center border-0 ' + iconClass
    toast.setAttribute('role', 'alert')
    toast.setAttribute('aria-live', 'assertive')
    toast.setAttribute('aria-atomic', 'true')

    toast.innerHTML = '<div class="d-flex"><div class="toast-body"></div><button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button></div>'
    var toastBodyEl = toast.querySelector('.toast-body')
    var titleHtml = sanitizeText(String(opts.title || ''))
    var textHtml = sanitizeText(String(opts.text || ''))
    var bodyHtml = sanitizeText(String(opts.html || ''))
    if (bodyHtml) {
      var parts = []
      if (titleHtml) parts.push('<div class="fw-semibold mb-1">' + titleHtml + '</div>')
      parts.push('<div>' + bodyHtml + '</div>')
      toastBodyEl.innerHTML = parts.join('')
    } else {
      var body = titleHtml || textHtml
      if (titleHtml && textHtml) body = titleHtml + ' - ' + textHtml
      toastBodyEl.textContent = body
    }
    host.appendChild(toast)

    var t = new window.bootstrap.Toast(toast, {
      delay: Number(opts.timer) > 0 ? Number(opts.timer) : 20000,
      autohide: opts.timer !== undefined ? Number(opts.timer) !== 0 : true
    })

    toast.addEventListener('hidden.bs.toast', function () {
      toast.remove()
    }, { once: true })

    t.show()

    return Promise.resolve({
      isConfirmed: false,
      isDenied: false,
      isDismissed: true,
      dismiss: 'timer'
    })
  }

  function createSwal(defaults) {
    var defaultOptions = mergeOptions({}, defaults)

    function findActiveByTarget(target) {
      if (!target) return getTopActive()
      for (var i = modalStack.length - 1; i >= 0; i--) {
        var entry = modalStack[i]
        if (!entry) continue
        if (target === entry.popup || target === entry.container) return entry
        if (target && target.nodeType === 1) {
          if (entry.container === target || entry.popup === target) return entry
          if (entry.container && entry.container.contains && entry.container.contains(target)) return entry
          if (entry.popup && entry.popup.contains && entry.popup.contains(target)) return entry
        }
      }
      return null
    }

    function forceRemoveActive(active) {
      if (!active) return
      // Do not call dispose() here: Bootstrap may still run async callbacks that expect _config.
      // A hard DOM detach is safer as a last-resort fallback for stuck modals.
      try {
        if (active.container) {
          active.container.classList.remove('show')
          active.container.style.display = 'none'
        }
      } catch (_error) {}
      try {
        if (active.container && active.container.parentNode) {
          active.container.parentNode.removeChild(active.container)
        }
      } catch (_error) {}
      modalStack = modalStack.filter(function (entry) {
        return entry && entry.container !== active.container
      })
      stripModalBackdrop()
    }

    function fire() {
      ensurePositionStyles()
      stripModalBackdrop()
      var incoming = normalizeFireArgs(Array.prototype.slice.call(arguments))
      var opts = mergeOptions(defaultOptions, incoming)

      if (opts.toast) {
        return showToast(opts)
      }

      var dom = buildModalDom(opts)
      document.body.appendChild(dom.container)

      function clampPopupBelowNavbar() {
        if (!dom.popup) return
        var navBottom = getPortalNavbarBottom()
        if (!(navBottom > 0)) return
        var rect = dom.popup.getBoundingClientRect()
        if (!rect || !Number.isFinite(rect.top)) return
        if (rect.top >= navBottom) return
        dom.popup.style.position = 'fixed'
        dom.popup.style.margin = '0'
        dom.popup.style.width = rect.width + 'px'
        dom.popup.style.maxWidth = rect.width + 'px'
        dom.popup.style.minWidth = rect.width + 'px'
        dom.popup.style.left = Math.max(0, rect.left) + 'px'
        dom.popup.style.top = navBottom + 'px'
        dom.popup.style.transform = 'none'
      }

      var dismissReason = null
      var resolved = false
      var onWindowResize = function () {
        applyNavbarClearance(dom.container, dom.dialog)
        requestAnimationFrame(clampPopupBelowNavbar)
      }

      var backdrop = false

      var keyboard = (opts.allowEscapeKey === false) ? false : true

      var enforceFocus = opts.enforceFocus === true
      var modalInstance = new window.bootstrap.Modal(dom.container, {
        backdrop: backdrop,
        keyboard: keyboard,
        focus: enforceFocus
      })

      var active = {
        container: dom.container,
        popup: dom.popup,
        modalInstance: modalInstance,
        validation: dom.validation
      }
      modalStack.push(active)

      function resolveOnce(resolve, payload) {
        if (resolved) return
        resolved = true
        resolve(payload)
      }

      return new Promise(function (resolve) {
        dom.container.addEventListener('shown.bs.modal', function () {
          stripModalBackdrop()
          onWindowResize()
          window.addEventListener('resize', onWindowResize)
          requestAnimationFrame(clampPopupBelowNavbar)
          if (typeof opts.didOpen === 'function') {
            opts.didOpen(dom.popup)
          }
        }, { once: true })

        dom.container.addEventListener('hide.bs.modal', function () {
          if (typeof opts.willClose === 'function') {
            opts.willClose(dom.popup)
          }
          if (!dismissReason) dismissReason = 'close'
        })

        dom.container.addEventListener('hidden.bs.modal', function () {
          stripModalBackdrop()
          window.removeEventListener('resize', onWindowResize)
          if (typeof opts.didClose === 'function') {
            opts.didClose(dom.popup)
          }
          modalStack = modalStack.filter(function (entry) {
            return entry && entry.container !== dom.container
          })
          dom.container.remove()
          resolveOnce(resolve, {
            isConfirmed: false,
            isDenied: false,
            isDismissed: true,
            dismiss: dismissReason || 'close'
          })
        }, { once: true })

        dom.cancelBtn.addEventListener('click', function () {
          dismissReason = 'cancel'
          modalInstance.hide()
        })

        dom.closeBtn.addEventListener('click', function () {
          dismissReason = 'close'
          modalInstance.hide()
        })

        dom.denyBtn.addEventListener('click', async function () {
          dom.validation.style.display = 'none'
          dom.validation.textContent = ''
          var value = false
          if (typeof opts.preDeny === 'function') {
            try {
              value = await opts.preDeny()
            } catch (_error) {
              value = false
            }
            if (value === false) {
              return
            }
          }
          resolveOnce(resolve, {
            isConfirmed: false,
            isDenied: true,
            isDismissed: false,
            value: value
          })
          modalInstance.hide()
        })

        dom.confirmBtn.addEventListener('click', async function () {
          dom.validation.style.display = 'none'
          dom.validation.textContent = ''
          var value
          if (typeof opts.preConfirm === 'function') {
            try {
              value = await opts.preConfirm()
            } catch (_error) {
              value = false
            }
            if (value === false) {
              return
            }
          }
          resolveOnce(resolve, {
            isConfirmed: true,
            isDenied: false,
            isDismissed: false,
            value: value
          })
          modalInstance.hide()
        })

        modalInstance.show()
        stripModalBackdrop()
      })
    }

    function close(target) {
      var active = findActiveByTarget(target)
      if (!active) return
      if (!active.modalInstance) {
        forceRemoveActive(active)
        return
      }
      try {
        active.modalInstance.hide()
      } catch (_error) {}
      setTimeout(function () {
        var stillExists = modalStack.some(function (entry) {
          return entry && entry.container === active.container
        })
        if (!stillExists) return
        forceRemoveActive(active)
      }, 300)
    }

    function getPopup() {
      var active = getTopActive()
      return active ? active.popup : null
    }

    function showValidationMessage(message) {
      var active = getTopActive()
      if (!active || !active.validation) return
      active.validation.textContent = sanitizeText(String(message || ''))
      active.validation.style.display = 'block'
    }

    function mixin(mixinDefaults) {
      var mergedDefaults = mergeOptions(defaultOptions, mixinDefaults)
      return createSwal(mergedDefaults)
    }

    return {
      fire: fire,
      close: close,
      getPopup: getPopup,
      showValidationMessage: showValidationMessage,
      mixin: mixin
    }
  }

  window.Swal = createSwal({})
})(window, document)
